'use client';

import React, { useRef, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from '@sharpr/ui';
import type { BookLine, SortField, BestLines, HiddenBy, Sport, MarketCode, LineupInfo } from '@sharpr/types';
import type { SortDirection, LiquidityThresholds } from '@sharpr/ui';
import { TableRow } from '../table/TableRow';
import { TableHeader } from '../table/TableHeader';
import {
  PRIMARY_COL_WIDTH,
  BEST_LINE_COL_WIDTH,
  SPORTSBOOK_COL_WIDTH,
  TABLE_ROW_HEIGHT,
} from '../table/tableLayout';
import type { OverlayTarget, ResearchTarget } from '../../types/research';
import type { GameWeather } from '@/services/weather/types';
import { useAnchoredRow } from '@/hooks/useAnchoredRow';

// PropRow interface for table data
export interface PropRow {
  id: string;
  /** Canonical `{mlb,nba}_players.player_id` — keys the game-log fetch. */
  playerId: string;
  primaryInfo: {
    title: string;
    subtitle?: string;
    tertiary?: string;
    imageUrl?: string;
    position?: string;
  };
  projectionInfo: {
    value: number | null; // null = no projection from Google Sheets
    unit: string;
    minutes?: number | null;
    market?: MarketCode;
  };
  books: BookLine[];
  bestLines?: BestLines;
  isUpdated?: boolean;
  inQuestionableGame?: boolean;
  isQuestionable?: boolean;
  pmf?: readonly number[];
  outPlayerIds?: string[];
  teamNotes?: string;
  playerNotes?: string;
  lineup?: LineupInfo;
  team?: string;
  opponent?: string;
  /** ISO commence time for the game — used by the line-movement overlay header
   *  to render game context (date + tip-off time). Threaded from
   *  `player.game.commenceTime` at row construction. */
  commenceTime?: string;
  /** Odds API event id — pins overlay line-movement to THIS row's game on
   *  doubleheader days. Threaded from `player.game.id` at row construction. */
  gameId?: string;
  /** Whether the player's team is home in this game. Drives the matchup
   *  rendering ("vs OPP" / "@ OPP") in the overlay header. */
  isHome?: boolean;
  weather?: GameWeather | null;
  /** Admin rows: why subscribers don't see this market's EV (an analyst player hide shows gray, the rest amber). */
  hiddenBy?: HiddenBy[];
  /** The player's line and projection in every market, whatever the table's filters show (overlay headers, research). */
  marketLines?: Record<string, number>;
  marketProjections?: Record<string, number>;
}

interface VirtualizedPropTableProps {
  rows: PropRow[];
  sportsbooks: string[];
  onLineMovementClick?: (target: OverlayTarget) => void;
  onOpenResearch?: (target: ResearchTarget) => void;
  className?: string;
  sortField?: SortField | null;
  sortDirection?: SortDirection;
  onSort?: (field: SortField) => void;
  onReorderSportsbooks?: (newOrder: string[]) => void;
  estimateRowHeight?: number;
  overscan?: number;
  sport?: Sport;
  isPanelCollapsed?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  liquidityThresholds?: LiquidityThresholds;
  /** Extra class(es) for every row's root element (e.g. demo fade-in hook). */
  rowClassName?: string;
}

export function VirtualizedPropTable({
  rows,
  sportsbooks,
  onLineMovementClick,
  onOpenResearch,
  className,
  sortField,
  sortDirection,
  onSort,
  onReorderSportsbooks,
  estimateRowHeight = TABLE_ROW_HEIGHT,
  overscan = 3,
  sport = 'nba',
  isPanelCollapsed,
  searchQuery,
  onSearchChange,
  liquidityThresholds,
  rowClassName,
}: VirtualizedPropTableProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  // First row showing an (outdoor) weather pill — the walkthrough spotlights it on
  // desktop. Sorted by EV, so it's usually at/near the top and within the rendered
  // window; -1 (none on the slate) leaves the step with no highlight.
  const tourWeatherRowIndex = useMemo(
    () => rows.findIndex((r) => r.weather && !r.weather.indoorVenue),
    [rows],
  );

  const { rangeExtractor, onAnchorChange } = useAnchoredRow(id => rows.findIndex(r => r.id === id));
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateRowHeight,
    overscan: overscan ?? 3,
    rangeExtractor,
  });
  
  const virtualItems = rowVirtualizer.getVirtualItems();
  
  // Calculate column widths (shared with OddsViewSkeleton via tableLayout)
  const primaryColWidth = PRIMARY_COL_WIDTH;
  const bestLineColWidth = BEST_LINE_COL_WIDTH;
  const sportsbookColWidth = SPORTSBOOK_COL_WIDTH;
  const totalWidth = primaryColWidth + bestLineColWidth + (sportsbooks.length * sportsbookColWidth);
  
  return (
    <div
      ref={parentRef}
      className={cn(
        "scrollbar-visible overflow-auto bg-bg dark:bg-bg-primary relative",
        className
      )}
    >
      {/* Sticky header */}
      <TableHeader
        sportsbooks={sportsbooks}
        primaryColWidth={primaryColWidth}
        bestLineColWidth={bestLineColWidth}
        sportsbookColWidth={sportsbookColWidth}
        totalWidth={totalWidth}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        onReorderSportsbooks={onReorderSportsbooks}
        isPanelCollapsed={isPanelCollapsed}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
      />
      
      {/* Table body container with virtual height */}
      <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: totalWidth, position: 'relative' }}>
        {/* Virtual rows */}
        {virtualItems.map((virtualRow) => {
          const row = rows[virtualRow.index];

          return (
            <TableRow
              key={row.id}
              row={row}
              sportsbooks={sportsbooks}
              primaryColWidth={primaryColWidth}
              bestLineColWidth={bestLineColWidth}
              sportsbookColWidth={sportsbookColWidth}
              totalWidth={totalWidth}
              virtualRowStart={virtualRow.start}
              virtualRowSize={virtualRow.size}
              onLineMovementClick={onLineMovementClick}
              onOpenResearch={onOpenResearch}
              onNotesOpenChange={onAnchorChange}
              sport={sport}
              liquidityThresholds={liquidityThresholds}
              tourTargetId={virtualRow.index === 0 ? 'tour-demo-odds-cell' : undefined}
              tourWeatherTargetId={virtualRow.index === tourWeatherRowIndex ? 'tour-demo-weather' : undefined}
              className={rowClassName}
            />
          );
        })}
      </div>
    </div>
  );
}